import { tv } from '@nuxt/ui/utils/tv';
import type * as componentThemes from '@/assets/css/themes/custom-components';

type ComponentThemes = typeof componentThemes;

type ComponentTheme = ComponentThemes[keyof ComponentThemes];

type ComponentThemeVariants<TTheme extends ComponentTheme> = TTheme extends {
    variants: infer TVariants;
}
    ? {
          [TVariant in keyof TVariants]?: [Extract<keyof TVariants[TVariant], string>] extends [never]
              ? keyof TVariants[TVariant]
              : Extract<keyof TVariants[TVariant], string> extends 'true' | 'false'
                ? boolean
                : keyof TVariants[TVariant];
      }
    : undefined;

type ComponentThemeSlots<TTheme extends ComponentTheme> = Record<
    keyof TTheme['slots'],
    (
        slotProps?: (ComponentThemeVariants<TTheme> extends undefined ? object : ComponentThemeVariants<TTheme>) & {
            class?: unknown;
            className?: unknown;
        }
    ) => string
>;

export function useComponentUi<TName extends keyof ComponentThemes>(
    name: TName,
    inputs?: MaybeRefOrGetter<ComponentThemeVariants<ComponentThemes[TName]>>
): ComputedRef<ComponentThemeSlots<ComponentThemes[TName]>> {
    const appConfig = useAppConfig();
    const resolveTheme = tv(appConfig.ui[name] as Parameters<typeof tv>[0]) as unknown as (
        variants?: ComponentThemeVariants<ComponentThemes[TName]>
    ) => ComponentThemeSlots<ComponentThemes[TName]>;

    return computed(() => resolveTheme(toValue(inputs)));
}
